/**
 * NotFound — graceful 404 page for unmatched routes.
 *
 * Replaces the previous behaviour where React Router silently rendered the
 * surrounding layout (cookie banner + nothing else) when a user hit a
 * missing route like `/funnel` or a typo. Public-aware: routes back to
 * `/login` for anonymous visitors and `/dashboard` for authenticated users
 * (or `/master` for super_admins / master_demo).
 */
import { Link } from "react-router-dom";
import { Compass, ArrowRight, Home } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

export default function NotFound() {
  const { user } = useAuth();
  const isAdmin = !!(user && ["super_admin", "admin", "master_demo"].includes(user.role));
  const primaryHref = !user ? "/login" : (isAdmin ? "/master" : "/dashboard");
  const primaryLabel = !user ? "Voltar ao login" : (isAdmin ? "Ir ao Master Console" : "Abrir Dashboard");

  return (
    <div
      data-testid="not-found-page"
      className="min-h-screen flex items-center justify-center px-6 py-16"
      style={{
        background: "radial-gradient(ellipse at 30% 20%, rgba(132,204,22,0.12), transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(201,162,78,0.10), transparent 50%), #0F0820",
      }}
    >
      <div className="max-w-xl w-full text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6"
          style={{
            background: "linear-gradient(135deg, rgba(132,204,22,0.18), rgba(201,162,78,0.18))",
            border: "1px solid rgba(201,162,78,0.35)",
            boxShadow: "0 12px 40px -16px rgba(132,204,22,0.4)",
          }}>
          <Compass className="w-7 h-7" style={{ color: "#C9A24E" }} />
        </div>

        <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em]"
          style={{ color: "#C9A24E" }}>
          404 · página não encontrada
        </p>

        <h1 className="mt-3 font-display font-black text-white tracking-tight leading-[1.05]"
          style={{ fontSize: "clamp(2.25rem, 5vw, 3.5rem)" }}>
          Esta rota saiu do mapa.
        </h1>

        <p className="mt-5 text-[14.5px] leading-relaxed mx-auto max-w-md"
          style={{ color: "rgba(255,255,255,0.7)" }}>
          O link que você abriu não existe (mais) no AgenteLead Pro. Pode ser
          um atalho antigo, um typo na URL ou uma página em construção.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 items-center justify-center">
          <Link
            to={primaryHref}
            data-testid="not-found-primary-cta"
            className="inline-flex items-center gap-2 font-bold text-[13.5px] px-6 py-3 rounded-md transition-all hover:-translate-y-0.5"
            style={{
              backgroundColor: "#C9A24E",
              color: "#0F0820",
              boxShadow: "0 10px 30px -12px rgba(201,162,78,0.6)",
            }}
          >
            {primaryLabel} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/"
            data-testid="not-found-secondary-cta"
            className="inline-flex items-center gap-2 font-semibold text-[13px] px-5 py-3 rounded-md border transition-colors"
            style={{
              borderColor: "rgba(255,255,255,0.18)",
              color: "rgba(255,255,255,0.85)",
              backgroundColor: "rgba(255,255,255,0.04)",
            }}
          >
            <Home className="w-4 h-4" /> Página inicial
          </Link>
        </div>

        <p className="mt-10 text-[11.5px]" style={{ color: "rgba(255,255,255,0.4)" }}>
          Se o problema persistir,{" "}
          <Link to="/contact" className="underline" style={{ color: "#C9A24E" }}>
            fale com nosso time
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
